import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import { connect } from 'react-redux';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';

import Badge from '../../../../../../../components/global/Badge';
import PhotoManagerSubscriptionTitle from './PhotoManagerSubscriptionTitle';
import Button from '../../../../../../../components/global/Button';
import { getErrorString } from '../../../../../../../util/normalize-errors';
import Alert from '../../../../../../../components/global/Alert';
import { requestConfirm as requestConfirmAction } from '../../../../../../../actions/modal-actions';
import {
  photoManagerSubscriptionCancel as photoManagerSubscriptionCancelMutation,
  photoManagerSubscriptionRestore as photoManagerSubscriptionRestoreMutation,
} from '../../../../../../../graphql/mutations/checkout';
import { AccountPhotoManagerSubscriptions } from '../../../../../../../graphql/queries/account';
import WarningIcon from '../../../../../../../components/icons/WarningIcon';

const propTypes = {
  className: PropTypes.string,
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
    default_payment_method: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  }),
  subscription: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  photoManagerSubscriptionCancel: PropTypes.func.isRequired,
  photoManagerSubscriptionRestore: PropTypes.func.isRequired,
  requestConfirm: PropTypes.func.isRequired,
};

const PhotoManagerActiveSubscription = ({
  className,
  account,
  subscription,
  photoManagerSubscriptionCancel,
  photoManagerSubscriptionRestore,
  requestConfirm,
}) => {
  const classes = classNames({
    'photo-manager-active-subscription': true,
    [className]: !!className,
  });

  const [submitting, setSubmitting] = useState(false);
  const [responseError, setResponseError] = useState('');
  const isTrial = subscription.trial_days > 0;
  const isCancelled = !!subscription.cancelled_at;
  const hasPaymentMethod = !!account?.default_payment_method?.id;

  const onCancel = () => {
    requestConfirm({
      message: (
        <Fragment>
          <p>
            <strong>Are you sure you want to cancel your subscription?</strong>
          </p>
          <p>
            You won’t be charged on your next billing date and your subscription
            will remain active until it expires on{' '}
            {moment(subscription.expires_at).format('MMMM Do, YYYY')}.
          </p>

          <Alert type="warning" className="d-flex align-items-start">
            <WarningIcon className="flex-none mr-2" size={24} />

            <div>
              <p className="mb-0">
                <strong>Please note:</strong> Your content will be removed from
                the system 7 days following the expiration of your subscription
                (
                {moment(subscription.expires_at)
                  .add(7, 'days')
                  .format('MMMM Do, YYYY')}
                ).
              </p>
            </div>
          </Alert>
        </Fragment>
      ),
      confirmText: 'Cancel my subscription',
      cancelText: 'No',
      onConfirm: async () => {
        setSubmitting(true);
        setResponseError('');

        try {
          await photoManagerSubscriptionCancel(subscription.id);
        } catch (error) {
          setResponseError(getErrorString(error));
        } finally {
          setSubmitting(false);
        }
      },
      onCancel: () => {},
    });
  };

  const onRestore = async () => {
    setSubmitting(true);
    setResponseError('');

    try {
      await photoManagerSubscriptionRestore(subscription.id);
    } catch (error) {
      setResponseError(getErrorString(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Fragment>
      <div className={classes}>
        <div className="photo-manager-active-subscription-content">
          <span className="photo-manager-active-subscription-label">
            Your active plan:{' '}
          </span>

          <PhotoManagerSubscriptionTitle
            className="text-dark text-extra-bold"
            subscription={subscription}
          />

          <span className="photo-manager-active-subscription-subtitle">
            {isTrial && (
              <Badge
                badgeStyle="purple"
                badgeSize="xs"
                className="photo-manager-active-subscription-trial-badge"
              >
                {subscription.trial_days} DAY TRIAL
              </Badge>
            )}

            {hasPaymentMethod ? (
              <span>Auto&ndash;renew: {isCancelled ? 'No' : 'Yes'}</span>
            ) : (
              <span>
                Free Trial ends{' '}
                {moment(subscription.expires_at).format('MM/DD/YY')}
              </span>
            )}
          </span>
        </div>

        {!isCancelled && (
          <div className="photo-manager-active-subscription-action">
            <Button
              btnStyle="white"
              btnSize="sm"
              onClick={onCancel}
              disabled={submitting}
            >
              {submitting ? (
                'Cancelling...'
              ) : (
                <>Cancel {isTrial ? 'Trial' : 'Plan'}</>
              )}
            </Button>
          </div>
        )}

        {isCancelled && (
          <div className="photo-manager-active-subscription-action">
            <Button
              btnStyle="white"
              btnSize="sm"
              onClick={onRestore}
              disabled={submitting}
            >
              {submitting ? 'Restoring...' : 'Restore'}
            </Button>
          </div>
        )}
      </div>

      {responseError && (
        <Alert type="danger" className="mt-2">
          Error: {responseError}
        </Alert>
      )}
    </Fragment>
  );
};

PhotoManagerActiveSubscription.propTypes = propTypes;

const mapDispatchToProps = {
  requestConfirm: requestConfirmAction,
};

const withPhotoManagerSubscriptionCancel = graphql(
  photoManagerSubscriptionCancelMutation,
  {
    props: ({ mutate }) => ({
      photoManagerSubscriptionCancel: id =>
        mutate({
          variables: {
            input: {
              photo_manager_subscription_id: id,
            },
          },
          refetchQueries: [
            {
              query: AccountPhotoManagerSubscriptions,
            },
          ],
        }),
    }),
  },
);

const withPhotoManagerSubscriptionRestore = graphql(
  photoManagerSubscriptionRestoreMutation,
  {
    props: ({ mutate }) => ({
      photoManagerSubscriptionRestore: id =>
        mutate({
          variables: {
            input: {
              photo_manager_subscription_id: id,
            },
          },
          refetchQueries: [
            {
              query: AccountPhotoManagerSubscriptions,
            },
          ],
        }),
    }),
  },
);

export default compose(
  connect(null, mapDispatchToProps),
  withPhotoManagerSubscriptionCancel,
  withPhotoManagerSubscriptionRestore,
)(PhotoManagerActiveSubscription);
